// note-name-source.ts — shared, ranked note-name candidate generator.
//
// The note-name suggestions the editor's [[wiki]] / [md](link) / #title autocomplete
// uses, factored out of editor/autocomplete.ts so other pickers can share the same
// sourcing + ranking + cache. `candidateNoteNames(partial)` returns up to 15:
// existing notes first (prefix matches before substring), then referenced-but-
// uncreated ("wanted") notes; dot-prefixed names excluded.
//
// A "wanted-notes" cache (names referenced via [[…]] / [..](..) but not yet created)
// refreshes every 30 s or on workspace switch / note creation.
// `installNoteNameCacheInvalidation()` wires those side-effects (a setNote hook + the
// workspace-switch channel) and must be called once, after window.NoteStorage exists.

let _wantedNotesCache: string[] | null = null;
let _wantedNotesCacheTime = 0;
let _wantedNotesCacheWorkspace: string | null = null;
const _WANTED_CACHE_TTL = 30000;

function _activeWorkspaceId(): string | null {
  try {
    return localStorage.getItem('current_workspace_id') || null;
  } catch {
    return null;
  }
}

async function getWantedNoteNames(): Promise<string[]> {
  const now = Date.now();
  const wsNow = _activeWorkspaceId();
  if (
    _wantedNotesCache &&
    _wantedNotesCacheWorkspace === wsNow &&
    now - _wantedNotesCacheTime < _WANTED_CACHE_TTL
  ) {
    return _wantedNotesCache;
  }
  const [allNotes, allNames] = await Promise.all([
    window.NoteStorage.getAllNotes(),
    window.NoteStorage.getAllNoteNames(),
  ]);
  const existingSet = new Set<string>(allNames);
  const wikiRe = /\[\[([^\]]+)\]\]/g;
  const mdLinkRe = /\[(?:[^\]]*)\]\(([^)]+)\)/g;
  const _isExternalTarget = (t: string) =>
    /^https?:\/\//i.test(t) ||
    /^mailto:/i.test(t) ||
    /^ftp:/i.test(t) ||
    t.startsWith('attachment:') ||
    t.startsWith('#') ||
    t.startsWith('/');
  const wanted = new Set<string>();
  for (const { content } of allNotes) {
    if (!content) continue;
    let m: RegExpExecArray | null;
    wikiRe.lastIndex = 0;
    while ((m = wikiRe.exec(content)) !== null) {
      const name = m[1].replace(/#{2,}.*$/, '').trim();
      if (!existingSet.has(name) && !name.startsWith('.')) wanted.add(name);
    }
    mdLinkRe.lastIndex = 0;
    while ((m = mdLinkRe.exec(content)) !== null) {
      // Strip a surrounding CommonMark angle-bracket pair first so the
      // space-preserving `[x](<New Idea>)` form is discovered too (#32), then
      // run the external-target skip on the unwrapped value.
      const raw = m[1].trim().replace(/^<(.*)>$/, '$1');
      if (!raw || _isExternalTarget(raw)) continue;
      let name: string;
      try {
        name = decodeURIComponent(raw).replace(/_/g, ' ').trim();
      } catch {
        name = raw.replace(/_/g, ' ').trim();
      }
      const hhIdx = name.indexOf('##');
      if (hhIdx !== -1) name = name.slice(0, hhIdx).trim();
      if (name && !existingSet.has(name) && !name.startsWith('.')) wanted.add(name);
    }
  }
  _wantedNotesCache = [...wanted];
  _wantedNotesCacheTime = now;
  _wantedNotesCacheWorkspace = wsNow;
  return _wantedNotesCache;
}

export interface NoteCandidates {
  /** existing notes, prefix-matches first, capped at 10. */
  existing: string[];
  /** referenced-but-uncreated notes, prefix-matches first, capped at 5. */
  wanted: string[];
  /** the full set of existing note names (for callers that need membership). */
  existingSet: Set<string>;
}

/** Up to 15 ranked candidates for `partialLower` (already lower-cased): up to 10
 *  existing notes (prefix matches first, then substring, then alpha), then up to 5
 *  referenced-but-uncreated notes; dot-prefixed names excluded. */
export async function candidateNoteNames(partialLower: string): Promise<NoteCandidates> {
  const [names, wanted] = await Promise.all([
    window.NoteStorage.getAllNoteNames(),
    getWantedNoteNames(),
  ]);
  const existingSet = new Set<string>(names);
  const rank = (a: string, b: string): number => {
    const as = a.toLowerCase().startsWith(partialLower);
    const bs = b.toLowerCase().startsWith(partialLower);
    return Number(bs) - Number(as) || a.localeCompare(b);
  };
  const matches = (n: string) => !partialLower || n.toLowerCase().includes(partialLower);
  const existing = (names as string[])
    .filter((n) => !n.startsWith('.') && matches(n))
    .sort(rank)
    .slice(0, 10);
  const filteredWanted = wanted
    .filter((n) => !existingSet.has(n) && matches(n))
    .sort(rank)
    .slice(0, 5);
  return { existing, wanted: filteredWanted, existingSet };
}

let _invalidationInstalled = false;
/** Wire wanted-cache invalidation: a workspace-switch BroadcastChannel + a setNote
 *  hook (so a freshly created name stops showing as "new"). Idempotent; call once
 *  after window.NoteStorage exists (the editor does so on autocomplete setup). */
export function installNoteNameCacheInvalidation(): void {
  if (_invalidationInstalled) return;
  _invalidationInstalled = true;
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const _wsChan = new BroadcastChannel('notes:workspace:change');
      _wsChan.addEventListener('message', (ev: MessageEvent) => {
        if (ev?.data?.type === 'switch') {
          _wantedNotesCache = null;
          _wantedNotesCacheTime = 0;
          _wantedNotesCacheWorkspace = null;
        }
      });
    } catch {
      /* unsupported — full reload covers same-tab switches */
    }
  }
  const _origSetNote = window.NoteStorage.setNote?.bind(window.NoteStorage);
  if (_origSetNote) {
    window.NoteStorage.setNote = async (name: any, content: any) => {
      _wantedNotesCache = null;
      return _origSetNote(name, content);
    };
  }
}

/** Test seam: reset the module-level wanted-cache between test cases. */
export function _resetNoteNameSourceForTest(): void {
  _wantedNotesCache = null;
  _wantedNotesCacheTime = 0;
  _wantedNotesCacheWorkspace = null;
}
